import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';

/**
 * Validates phone number in E.164 format
 * @param {string} phone - Phone number to validate
 * @returns {object} - { isValid: boolean, formatted: string, error: string }
 */
export const validatePhone = (phone) => {
  try {
    // Check basic format
    if (!phone || phone.trim().length === 0) {
      return {
        isValid: false,
        formatted: '',
        error: 'Phone number is required'
      };
    }

    // Remove all spaces and special characters except +
    const cleanPhone = phone.replace(/[^\d+]/g, '');

    // Check if it starts with +
    if (!cleanPhone.startsWith('+')) {
      return {
        isValid: false,
        formatted: cleanPhone,
        error: 'Phone must start with + and country code (e.g., +1 for USA)'
      };
    }

    // Use libphonenumber-js for validation
    if (!isValidPhoneNumber(cleanPhone)) {
      return {
        isValid: false,
        formatted: cleanPhone,
        error: 'Invalid phone number format'
      };
    }

    // Parse and format
    const phoneNumber = parsePhoneNumber(cleanPhone);
    
    return {
      isValid: true,
      formatted: phoneNumber.format('E.164'),
      error: ''
    };
  } catch (error) {
    return {
      isValid: false,
      formatted: phone,
      error: 'Invalid phone number'
    };
  }
};

/**
 * Format phone number for display
 * @param {string} phone - Phone number in E.164 format
 * @returns {string} - Formatted phone number
 */
export const formatPhoneDisplay = (phone) => {
  try {
    const phoneNumber = parsePhoneNumber(phone);
    return phoneNumber.formatInternational();
  } catch {
    return phone;
  }
};

/**
 * Validates verification code
 * @param {string} code - Verification code
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validateCode = (code) => {
  const cleanCode = code.replace(/\D/g, '');
  
  if (cleanCode.length !== 6) {
    return {
      isValid: false,
      error: 'Code must be 6 digits'
    };
  }
  
  return {
    isValid: true,
    error: ''
  };
};

/**
 * Validates name fields
 * @param {string} name - Name to validate
 * @param {string} fieldName - Field name for error message
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validateName = (name, fieldName = 'Name') => {
  const trimmedName = name.trim();
  
  if (trimmedName.length === 0) {
    return {
      isValid: false,
      error: `${fieldName} is required`
    };
  }
  
  if (trimmedName.length < 2) {
    return {
      isValid: false,
      error: `${fieldName} must be at least 2 characters`
    };
  }
  
  if (!/^[a-zA-Z\s'-]+$/.test(trimmedName)) {
    return {
      isValid: false,
      error: `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`
    };
  }
  
  return {
    isValid: true,
    error: ''
  };
};

/**
 * Validates email (optional field)
 * @param {string} email - Email to validate
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validateEmail = (email) => {
  if (!email || email.trim().length === 0) {
    return { isValid: true, error: '' }; // Email is optional
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!emailRegex.test(email)) {
    return {
      isValid: false,
      error: 'Invalid email format'
    };
  }
  
  return {
    isValid: true,
    error: ''
  };
};
